// Microsoft Advertising click id (`msclkid`) capture. Auto-tagging appends it
// to the landing URL after a Microsoft ad click. Storing the most recent value
// on the payment row lets a purchase be uploaded to Microsoft as an offline
// conversion when the browser UET tag never saw it. It lives in its own
// cookie rather than inside the last-touch attribution cookie because that one
// is rewritten by any later referrer visit, which would drop the click id
// before the buyer pays.

export const MSCLKID_COOKIE = "hyred_msclkid";

// Microsoft's suggested retention for the stored click id.
const MSCLKID_MAX_AGE_SECONDS = 60 * 60 * 24 * 90;

const HEX_32 = /^[0-9a-f]{32}$/;

/**
 * Returns the click id as 32 lowercase hex characters, the form auto-tagging
 * puts on the landing URL and offline conversion import expects. A dashed UUID
 * is accepted and folded to the same form. Anything else is rejected, so a
 * tampered cookie or query string never reaches the database.
 */
export function normalizeMsclkid(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }

  const hex = value.trim().toLowerCase().replace(/-/g, "");

  return HEX_32.test(hex) ? hex : null;
}

/**
 * Stores the click id from the current URL, overwriting any older one so the
 * cookie always holds the latest Microsoft click. A URL without a valid
 * `msclkid` leaves the existing cookie alone.
 */
export function captureMsclkidCookieFromBrowser(searchParams: URLSearchParams) {
  if (typeof document === "undefined") {
    return;
  }

  const msclkid = normalizeMsclkid(searchParams.get("msclkid"));

  if (!msclkid) {
    return;
  }

  const secure = window.location.protocol === "https:" ? "; secure" : "";
  document.cookie = `${MSCLKID_COOKIE}=${msclkid}; path=/; max-age=${MSCLKID_MAX_AGE_SECONDS}; samesite=lax${secure}`;
}
